import { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Loader from "./Loader";
import ErrorMessage from "./ErrorMessage";
import ReviewForm from "./ReviewForm";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = Cookies.get("ecom_user_token");
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/order/my-orders`,
          {
            params: { page, limit },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setOrders(response.data.orders || []);
        setTotalPages(Number(response.data.totalPages) || 1);
        setPage(Number(response.data.currentPage) || 1);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch orders");
        toast.error("Failed to load orders");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [page, limit]);

  const getPaginationPages = () => {
    const pages = [];
    const delta = 1;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    if (page > delta + 2) {
      pages.push("...");
    }

    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(totalPages - 1, page + delta);
      i++
    ) {
      pages.push(i);
    }

    if (page < totalPages - (delta + 1)) {
      pages.push("...");
    }

    pages.push(totalPages);

    return pages;
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const formatINR = (amount) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const getPaymentLabel = (method) => {
    if (method === "cod") return "Cash on Delivery";
    if (method === "razorpay") return "Online Payment";
    return "Unknown";
  };

  const getPaymentStatusStyle = (status) =>
    status === "completed"
      ? "bg-green-50 text-green-700 border-green-200"
      : "bg-yellow-50 text-yellow-800 border-yellow-200";

  const getCurrentStatus = (product, tracking) => {
    if (product.status === "cancelled") {
      return {
        text: "Cancelled",
        color: "bg-red-100 text-red-800",
        description: "This order has been cancelled",
        isRefunded: product.isRefunded,
      };
    }

    if (!tracking) {
      return {
        text: "Processing",
        color: "bg-yellow-100 text-yellow-800",
        description: "Your order is being prepared",
      };
    }

    switch (tracking.status) {
      case "shipped":
        return {
          text: "Shipped",
          color: "bg-blue-100 text-blue-800",
          description: `Shipped via ${tracking.carrier}`,
        };
      case "in_transit":
        return {
          text: "In Transit",
          color: "bg-purple-100 text-purple-800",
          description: "Your item is on the way",
        };
      case "delivered":
        return {
          text: "Delivered",
          color: "bg-green-100 text-green-800",
          description: "Your item has been delivered",
        };
      default:
        return {
          text: "Processing",
          color: "bg-yellow-100 text-yellow-800",
          description: "Seller is preparing your order",
        };
    }
  };

  const handleReviewClick = (item) => {
    if (!item?.productId?._id || !item?.variantId) {
      toast.error("Product info missing");
      return;
    }

    // find the purchased variant
    const variant = item.productId.variants.find(
      (v) => v._id === item.variantId,
    );

    setSelectedProduct({
      productId: item.productId._id,
      variantId: item.variantId,
      orderItemId: item._id,
      name: item.productId.name,
      image: variant?.images?.[0],
    });

    setShowReviewModal(true);
  };

  const handleReviewSubmit = async (reviewData) => {
    try {
      const token = Cookies.get("ecom_user_token");
      const formData = new FormData();

      formData.append("ratings", reviewData.rating);
      formData.append("reviews", reviewData.reviewText);
      formData.append("productId", selectedProduct.productId);

      reviewData.reviewImages.forEach((image, index) => {
        formData.append("productImages", image);
      });

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/rating/addProductRating`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      toast.success("Review submitted successfully!");
      setShowReviewModal(false);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit review");
      return false;
    }
  };

  if (loading) return <Loader fullScreen />;
  if (error) return <ErrorMessage message={error} />;

  const flattenOrdersBySeller = () => {
    return orders.flatMap((order) =>
      order?.sellers?.map((seller) => {
        // Filter out broken products safely
        const validProducts =
          seller?.products?.filter((p) => p?.productId) || [];

        return {
          orderId: order._id,
          createdAt: order.createdAt,
          invoice: order.invoice,
          shippingAddress: order.shippingAddress,
          payment: order.payment,
          tracking: seller.tracking,
          sellerStatus: seller.sellerStatus,
          products: validProducts,
        };
      }),
    );
  };

  const MAX_IMAGES_DESKTOP = 2;

  return (
    <div
      className="bg-[#fdfaf6] rounded-2xl shadow-xl overflow-hidden p-3 md:p-8 border border-[#eaddcf] relative"
      style={{
        backgroundImage:
          "url('https://www.transparenttextures.com/patterns/paper-fibers.png')",
      }}
    >
      <h2 className="text-2xl md:text-3xl font-serif font-extrabold text-[#5d4037] mb-8 border-b-2 border-[#efe0d0] pb-4">
        Order History
      </h2>

      {orders.length === 0 ? (
        <div className="text-center py-12 md:py-20 bg-[#efe0d0]/20 rounded-2xl border-2 border-dashed border-[#eaddcf]">
          <div className="text-4xl mb-4">📦</div>
          <h3 className="text-lg md:text-xl font-serif font-bold text-[#8d6e63]">
            No Crafts Found
          </h3>
          <p className="mt-2 text-sm md:text-base text-[#a1887f] italic font-serif">
            Your workshop floor is currently empty.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {flattenOrdersBySeller().map((item) => {
            const { products, tracking, orderId, createdAt, payment, invoice } =
              item;

            if (!products.length) return null;

            const productCount = products.length;

            const status = getCurrentStatus(products[0], tracking);

            return (
              <div
                key={orderId}
                onClick={() => navigate(`/orders/${orderId}`)}
                className="group relative border-2 border-[#eaddcf] bg-white rounded-xl p-3 md:p-6 hover:border-[#8d6e63] hover:shadow-xl transition-all cursor-pointer overflow-hidden"
              >
                {/* Wooden Side Accent */}
                <div className="absolute top-0 left-0 w-1.5 md:w-2 h-full bg-[#8d6e63] opacity-20 group-hover:opacity-100 transition-opacity" />

                {/* HEADER INFO - Better wrap behavior for mobile */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-5 text-xs font-serif text-[#6d4c41]">
                  <div className="flex items-center gap-3">
                    <p>
                      <span className="font-bold">Order Date:</span>{" "}
                      {formatDate(createdAt)}
                    </p>

                    <span className="px-2 py-1 border rounded-sm bg-[#f5ebe0] text-[#5d4037] font-bold uppercase">
                      {productCount} {productCount === 1 ? "Item" : "Items"}
                    </span>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center gap-2 order-1 sm:order-2">
                    <span className="px-2 py-1 border rounded-sm bg-[#fdfaf6] text-[#5d4037] font-bold uppercase whitespace-nowrap">
                      {getPaymentLabel(payment?.method)}
                    </span>
                    <span
                      className={`px-2 py-1 border rounded-sm font-bold uppercase whitespace-nowrap ${getPaymentStatusStyle(
                        payment?.status,
                      )}`}
                    >
                      {payment?.status}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row items-start gap-5 md:gap-6">
                  {/* PRODUCT IMAGES - Scrollable on mobile if many products */}
                  <div className="flex-shrink-0 flex gap-2 w-full md:w-auto overflow-x-auto md:overflow-visible scrollbar-hide">
                    {products.slice(0, MAX_IMAGES_DESKTOP).map((product) => {
                      const variant = product.productId?.variants?.[0];

                      return (
                        <div
                          key={product._id}
                          className="w-20 h-20 flex-shrink-0 p-1 bg-[#efe0d0] rounded-lg shadow-inner"
                        >
                          <img
                            src={variant?.images?.[0]}
                            alt={product.productId.name}
                            className="w-full h-full object-cover rounded grayscale-[20%] transition-all"
                          />
                        </div>
                      );
                    })}

                    {/* +N more (desktop only) */}
                    {products.length > MAX_IMAGES_DESKTOP && (
                      <div className="flex w-10 md:w-20 h-20 flex-shrink-0 items-center justify-center bg-[#efe0d0] rounded-lg text-sm font-medium shadow-inner">
                        +{products.length - MAX_IMAGES_DESKTOP}
                      </div>
                    )}
                  </div>

                  {/* DETAILS */}
                  <div className="flex-grow w-full">
                    <div className="flex flex-col sm:flex-row justify-between items-start border-b border-[#f5ebe0] pb-3 mb-3 gap-2">
                      <div className="max-w-md">
                        <h3 className="font-serif font-bold text-[#5d4037] text-base md:text-lg leading-tight line-clamp-2">
                          {products.map((p) => p.productId.name).join(", ")}
                        </h3>
                        <p className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-[#a1887f] mt-1">
                          Manifest No. #{orderId.slice(-8)}
                        </p>
                      </div>

                      <p className="font-serif font-extrabold text-[#5d4037] text-lg">
                        ₹
                        {formatINR(
                          products.reduce((sum, p) => sum + p.total, 0),
                        )}
                      </p>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                      <div className="space-y-2">
                        {/* ORDER STATUS */}
                        <div
                          className={`inline-flex px-3 py-1 rounded-sm text-[10px] font-bold uppercase border ${status.text.toLowerCase().includes("cancel")
                              ? "bg-red-50 text-red-800 border-red-200"
                              : "bg-[#5d4037] text-[#fdfaf6] border-[#3e2723]"
                            }`}
                        >
                          {status.text}
                        </div>

                        <p className="font-serif text-sm italic text-[#6d4c41] leading-relaxed">
                          {status.description}
                        </p>
                      </div>

                      {/* ACTION BUTTON - Full width on mobile */}

                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReviewClick(products[0]);
                          }}
                          className="w-full md:w-auto text-sm font-serif font-bold text-[#8d6e63] hover:text-[#5d4037] transition-colors"
                        >
                          <div className="px-4 py-3 md:py-2 bg-[#efe0d0]/50 rounded-lg border border-[#eaddcf] text-center">
                            Write a Testimonial
                          </div>
                        </button>

                        {
                          invoice?.url &&
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(invoice?.url, "_blank");
                            }}
                            className="w-full md:w-auto text-sm font-serif font-bold text-[#8d6e63] hover:text-[#5d4037] transition-colors"
                          >
                            <div className="px-4 py-3 md:py-2 bg-[#efe0d0]/50 rounded-lg border border-[#eaddcf] text-center">
                              Download Invoice
                            </div>
                          </button>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 🔹 Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-1 mt-6 flex-wrap">
          {/* Previous Button */}
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="group flex items-center justify-center gap-1 px-2 py-1.5 sm:px-4 sm:py-2 rounded-md sm:rounded-lg border-[1px] sm:border-2 border-[#eaddcf] 
							bg-[#fdfaf6] text-[#5d4037] font-serif font-bold text-xs sm:text-base
							hover:bg-[#efe0d0] hover:border-[#8d6e63]
							disabled:opacity-40 disabled:cursor-not-allowed
							transition-all min-w-[36px] sm:min-w-[44px]"
          >
            <span className="text-base sm:text-lg group-hover:-translate-x-0.5 transition-transform">
              ←
            </span>
            <span className="hidden xs:inline sm:inline">Prev</span>
          </button>

          {/* Page Numbers Container */}
          <div className="flex items-center gap-0.5 sm:gap-2">
            {getPaginationPages().map((p, index) =>
              p === "..." ? (
                <span
                  key={`dots-${index}`}
                  className="px-0.5 sm:px-2 text-[#8d6e63] select-none text-xs sm:text-base"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`min-w-[32px] h-8 sm:min-w-[40px] sm:h-10 flex items-center justify-center border rounded transition text-xs sm:text-base ${page === p
                      ? "bg-[#5d4037] text-white border-[#5d4037]"
                      : "hover:bg-[#efe0d0] border-[#eaddcf] text-[#5d4037]"
                    } ${
                    // Hide more numbers on mobile to prevent row wrapping
                    typeof p === "number" &&
                      Math.abs(page - p) > 0 &&
                      p !== 1 &&
                      p !== totalPages
                      ? "hidden sm:flex"
                      : "flex"
                    }`}
                >
                  {p}
                </button>
              ),
            )}
          </div>

          {/* Next Button */}
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="group flex items-center justify-center gap-1 px-2 py-1.5 sm:px-4 sm:py-2 rounded-md sm:rounded-lg border-[1px] sm:border-2 border-[#eaddcf] 
							bg-[#fdfaf6] text-[#5d4037] font-serif font-bold text-xs sm:text-base
							hover:bg-[#efe0d0] hover:border-[#8d6e63]
							disabled:opacity-40 disabled:cursor-not-allowed
							transition-all min-w-[36px] sm:min-w-[44px]"
          >
            <span className="hidden xs:inline sm:inline">Next</span>
            <span className="text-base sm:text-lg group-hover:translate-x-0.5 transition-transform">
              →
            </span>
          </button>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedProduct && (
        <ReviewForm
          productId={selectedProduct.productId}
          productName={selectedProduct.name}
          productImage={selectedProduct.image}
          onClose={() => setShowReviewModal(false)}
          onReviewSubmit={handleReviewSubmit}
        />
      )}
    </div>
  );
};

export default Orders;
